/**
 * 병사·영웅 그림 시트의 모양과, 렌더러가 부르는 불러오기.
 *
 * 그림은 림월드식이다(`rim-sheet.ts`, `rim-bake.ts`): 팔다리 없는 층(몸·머리·옷·갑옷·투구·말)을 방향마다 한 장, 무기는
 * 오른쪽을 향한 한 장으로 두고, 걷기·공격·피격·쓰러짐은 절차로 굽는다. 차림마다 64칸 33개: 서기 4방향 · 걷기 A 4 · 걷기 B 4 ·
 * 피격 · 피격 복귀 · 쓰러짐 1–3 · 공격 4방향 × 4. 방향 순서는 정면·왼쪽 보기·오른쪽 보기·뒷면.
 *
 * 전에는 인물마다 구운 시트(`units.png` 67명 × 17칸, 공격은 따로 `attacks.png`)였다. 복장·무기·무술이 늘 때마다 그림이
 * 곱으로 늘어(장수 착장 조합만 백만 칸이 넘었다) 층을 얹어 굽는 방식으로 바꾸고 옛 시트는 걷어 냈다.
 *
 * 렌더링 전용이다. 시뮬레이션은 이 파일을 모른다.
 */
import type { Texture } from 'pixi.js';
import type { CombatantSpec } from '../sim/troops.ts';
import { loadRimSheet } from './rim-sheet.ts';

/** 방패 손: 스프라이트 기준점(발 가운데)에서 칸 픽셀로 잰 거리. 화면에서는 스프라이트 배율을 곱한다. */
export interface HandPos {
  readonly dx: number;
  readonly dy: number;
}

/** 방향마다 방패 손. 그 칸의 손을 모르면 null — 렌더러가 방향별 기본 자리를 쓴다. */
export interface UnitHands {
  stand: (HandPos | null)[];
  walkA: (HandPos | null)[];
  walkB: (HandPos | null)[];
}

/** 방향: 정면 · 왼쪽 보기 · 오른쪽 보기 · 뒷면(시트의 열 순서). */
export const Dir = { Front: 0, Left: 1, Right: 2, Back: 3 } as const;
export type Dir = (typeof Dir)[keyof typeof Dir];

export interface UnitFrames {
  stand: Texture[];
  walkA: Texture[];
  walkB: Texture[];
  hit: Texture;
  hit2: Texture;
  death: Texture[];
  hands: UnitHands;
  /** 공격 동작 [방향][칸]. 없으면 null(서기·걷기 그대로). */
  attack: Texture[][] | null;
}

export interface UnitSheet {
  readonly cell: number;
  /** 발 줄의 아래 모서리를 칸 높이로 나눈 값. 스프라이트 anchor.y로 쓴다. */
  readonly footAnchor: number;
  /** 공격 칸 하나를 보여 주는 시간(ms). */
  readonly attackMs: number;
  /**
   * 치는 순간(쿨다운이 올라가는 틱)에 보일 공격 칸. 없으면 0(처음부터). 림월드식은 1 — 준비 칸(0)은 치기 직전에 미리 보인다
   * (`Renderer.pickFrame`). 옛 시트처럼 처음부터 재생하면 피해가 들어간 뒤에야 창을 들어 올렸다.
   */
  readonly attackHit?: number;
  /** 이 인물을 그릴 열쇠(차림 열쇠 `lookKey`). 없는 인물이면 null — 렌더러가 예전 코드 그림(`atlas.ts`)으로 그린다. */
  keyFor(spec: CombatantSpec): string | null;
  /**
   * 적의 대장·부장 `leaderId`(`meta/foe-leaders.ts`)의 차림 열쇠 — 인물마다 다른 모습이다(`looks.foeLeaderLook`). 차림을
   * 정해 두지 않은 id면 null(부르는 쪽이 `keyFor`로 그린다).
   */
  keyForFoeLeader?(leaderId: string, spec: CombatantSpec): string | null;
  frames(key: string, enemy: boolean): UnitFrames | null;
}

/**
 * 적군본: 채도가 높고 확실히 빨간 것(우리 편 표시 천)만 초록으로. `art/tools/motion.py`의 `swap_red`와 같은 규칙이다.
 * 처음 규칙(채도 0.3·색상 ±0.05)은 붉은 기가 도는 갈색 머리카락·가죽까지 초록으로 바꿨다.
 */
export function swapRed(r: number, g: number, b: number): [number, number, number] | null {
  const rf = r / 255, gf = g / 255, bf = b / 255;
  const max = Math.max(rf, gf, bf), min = Math.min(rf, gf, bf);
  const l = (max + min) / 2;
  if (max === min) return null;
  const d = max - min;
  const s = l <= 0.5 ? d / (max + min) : d / (2 - max - min);
  let h: number;
  if (max === rf) h = ((gf - bf) / d) / 6;
  else if (max === gf) h = (2 + (bf - rf) / d) / 6;
  else h = (4 + (rf - gf) / d) / 6;
  if (h < 0) h += 1;
  if (!(s > 0.5 && (h < 0.035 || h > 0.95) && l > 0.12)) return null;
  return hlsToRgb(0.33, l * 0.95, s * 0.75);
}

function hlsToRgb(h: number, l: number, s: number): [number, number, number] {
  const m2 = l <= 0.5 ? l * (1 + s) : l + s - l * s;
  const m1 = 2 * l - m2;
  const v = (hue: number): number => {
    hue = ((hue % 1) + 1) % 1;
    if (hue < 1 / 6) return m1 + (m2 - m1) * hue * 6;
    if (hue < 0.5) return m2;
    if (hue < 2 / 3) return m1 + (m2 - m1) * (2 / 3 - hue) * 6;
    return m1;
  };
  return [Math.floor(v(h + 1 / 3) * 255), Math.floor(v(h) * 255), Math.floor(v(h - 1 / 3) * 255)];
}

/** 적군본(붉은 천 → 초록). 차림마다 구운 캔버스에 칠한다(`rim-sheet.ts`). */
export function enemyRecolor(img: HTMLImageElement | HTMLCanvasElement): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = img.width;
  c.height = img.height;
  const g = c.getContext('2d', { willReadFrequently: true })!;
  g.drawImage(img, 0, 0);
  const data = g.getImageData(0, 0, c.width, c.height);
  const px = data.data;
  // 같은 색이 수만 번 나온다. 한 번 잰 색은 기억해 둔다.
  const memo = new Map<number, number>();
  for (let i = 0; i < px.length; i += 4) {
    if (px[i + 3] === 0) continue;
    const rgb = (px[i] << 16) | (px[i + 1] << 8) | px[i + 2];
    let out = memo.get(rgb);
    if (out === undefined) {
      const sw = swapRed(px[i], px[i + 1], px[i + 2]);
      out = sw ? (sw[0] << 16) | (sw[1] << 8) | sw[2] : rgb;
      memo.set(rgb, out);
    }
    if (out !== rgb) {
      px[i] = out >> 16;
      px[i + 1] = (out >> 8) & 255;
      px[i + 2] = out & 255;
    }
  }
  g.putImageData(data, 0, 0);
  return c;
}

/**
 * 공격 칸 번호: 공격을 시작한 지 `elapsed`ms 뒤, 한 칸 `ms`씩 `frames`칸. 끝났거나 시작 전이면 -1(서기·걷기로 돌아간다).
 */
export function attackFrameAt(elapsed: number, ms: number, frames: number): number {
  if (!(ms > 0) || elapsed < 0) return -1;
  const k = Math.floor(elapsed / ms);
  return k < frames ? k : -1;
}

/**
 * 시트를 불러온다. 층 그림을 못 받았거나 이 GPU의 텍스처 한계(`maxSize`)보다 크면 null — 렌더러는 예전 코드 그림
 * (`atlas.ts`)으로 그린다. 한계를 넘는 텍스처는 만들어는 지고 그릴 때 올리다 실패해 빈 병사가 된다.
 */
export function loadUnitSheet(maxSize: number, base: string = import.meta.env.BASE_URL): Promise<UnitSheet | null> {
  return loadRimSheet(maxSize, enemyRecolor, base);
}
